import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Loading, StatusPill } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Sparkles, ExternalLink, Download } from "lucide-react";

export default function BillingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [note, setNote] = useState("");
  const [sub, setSub] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [busy, setBusy] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [searchParams, setSearchParams] = useSearchParams();
  const keepAsked = searchParams.get("keep") === "1";
  const [kept, setKept] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api.get("/billing/plans"), api.get("/billing/subscription")]);
      setPlans(p.data.plans);
      setNote(p.data.note);
      setSub(s.data.subscription);
      setCanManage(!!s.data.can_manage);
    } catch (e) {
      setPlans([]);
    }
    try {
      const inv = await api.get("/billing/invoices");
      setInvoices(inv.data.invoices || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const subscribe = async (lookup_key, trial = false) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin, trial });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(null);
    }
  };
  const change = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/change", { lookup_key });
      setSub(data.subscription);
      toast.success("Plan updated.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try {
      const { data } = await api.post("/billing/cancel");
      setSub(data.subscription);
      toast.success("Subscription will cancel at the end of the period.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  const keepPlan = async () => {
    setBusy("keep");
    try {
      const { data } = await api.post("/billing/keep-plan");
      setKept(data);
      toast.success(`You're all set — ${data.plan_name || "your plan"} continues${data.continues_on ? ` on ${data.continues_on}` : ""}.`);
      const next = new URLSearchParams(searchParams);
      next.delete("keep");
      setSearchParams(next, { replace: true });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(null); }
  };

  if (plans === null) return <Loading label="Loading plans…" />;
  const currentKey = sub?.lookup_key;
  const annual = cycle === "annual";

  return (
    <div data-testid="billing-page">
      <PageHeader title="Plans & Billing" subtitle={note} />

      <div className="mb-6 inline-flex items-center rounded-full border border-slate-700 bg-slate-900/60 p-1" data-testid="billing-cycle-toggle">
        <button onClick={() => setCycle("monthly")} data-testid="cycle-monthly"
          className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${!annual ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>Monthly</button>
        <button onClick={() => setCycle("annual")} data-testid="cycle-annual"
          className={`flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${annual ? "bg-blue-600 text-white" : "text-slate-300 hover:text-white"}`}>
          Annual <span className="rounded-full bg-emerald-500/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">2 mo free</span>
        </button>
      </div>

      {sub && canManage && (keepAsked || kept) && ["trialing", "active"].includes(sub.status) && (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="keep-plan-panel">
          {kept ? (
            <div className="text-sm text-emerald-200" data-testid="keep-plan-confirmed">
              <span className="font-semibold text-white">Plan confirmed.</span> {kept.plan_name} continues{kept.continues_on ? ` after your trial on ${kept.continues_on}` : ""}{kept.price ? ` at ${kept.price}` : ""}. Nothing else to do.
            </div>
          ) : (
            <>
              <div className="text-sm text-emerald-100">
                <div className="font-semibold text-white">Keep your {sub.plan_name} plan?</div>
                Your trial ends{sub.current_period_end ? ` on ${new Date(sub.current_period_end * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}` : " soon"}. Confirm and your fleet, drivers and history continue without interruption.
              </div>
              <Button onClick={keepPlan} disabled={busy === "keep"} data-testid="keep-plan-btn"
                className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500">
                {busy === "keep" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Keep my plan
              </Button>
            </>
          )}
        </div>
      )}

      {sub && (
        <div className="mb-6 flex flex-col gap-3 rounded-xl border border-blue-600/40 bg-blue-600/10 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="current-subscription">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-blue-300">Current plan</div>
            <div className="font-display text-xl font-bold text-white">
              {sub.plan_name}
              <span className="ml-2 text-sm font-normal text-slate-400">
                · {sub.status}{sub.cancel_at_period_end ? " (cancels at period end)" : ""}
              </span>
            </div>
          </div>
          {canManage && !sub.cancel_at_period_end && (
            <Button onClick={cancel} disabled={busy === "cancel"} variant="outline" data-testid="cancel-subscription-btn"
              className="gap-2 border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200">
              {busy === "cancel" ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Cancel subscription
            </Button>
          )}
        </div>
      )}

      {!canManage && (
        <div className="mb-6 rounded-lg border border-slate-800 bg-[#111827] px-4 py-3 text-sm text-slate-400" data-testid="billing-readonly-note">
          Only owners and administrators can change the plan. You can review the options below.
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2 xl:grid-cols-4">
        {plans.map((plan) => {
          const lk = (annual && plan.lookup_key_annual) ? plan.lookup_key_annual : plan.lookup_key;
          const isCurrent = currentKey && (currentKey === plan.lookup_key || currentKey === plan.lookup_key_annual);
          return (
            <div key={plan.id} data-testid={`plan-card-${plan.id}`}
              className={`relative flex flex-col rounded-2xl border p-6 ${plan.popular ? "border-blue-500 bg-[#111a2e]" : "border-slate-800 bg-[#111827]"} ${isCurrent ? "ring-2 ring-emerald-500/50" : ""}`}>
              {plan.popular && (
                <span className="absolute -top-3 left-6 rounded-full bg-emerald-500 px-3 py-0.5 text-[11px] font-bold uppercase tracking-wide text-white" data-testid="most-popular-badge">
                  Most Popular
                </span>
              )}
              <div className="font-display text-lg font-bold text-white">{plan.name}</div>
              <p className="mt-1 text-sm text-slate-400">{plan.tagline}</p>
              <div className="mt-4">
                {plan.custom ? (
                  <div className="font-display text-4xl font-bold text-white">Custom</div>
                ) : (
                  <div className="flex items-end gap-1">
                    <span className="font-display text-4xl font-bold text-white">${annual ? (plan.price_annual ?? plan.price * 10).toLocaleString() : plan.price}</span>
                    <span className="mb-1.5 text-sm text-slate-500">{annual ? "/yr" : "/mo"}</span>
                  </div>
                )}
                <div className="mt-1 text-sm font-semibold text-blue-400">{plan.vehicles}</div>
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {f}
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {plan.custom ? (
                  <Button onClick={() => navigate("/request-demo")} data-testid={`plan-contact-${plan.id}`}
                    className="w-full gap-2 bg-slate-100 text-slate-900 hover:bg-white">Contact sales</Button>
                ) : isCurrent ? (
                  <Button disabled data-testid={`plan-current-${plan.id}`}
                    className="w-full bg-emerald-600/20 text-emerald-300 hover:bg-emerald-600/20">Current plan</Button>
                ) : sub ? (
                  <Button onClick={() => change(lk)} disabled={!canManage || busy === lk} data-testid={`plan-switch-${plan.id}`}
                    className="w-full gap-2 bg-blue-600 text-white hover:bg-blue-500">
                    {busy === lk ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Switch to this plan
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button onClick={() => subscribe(lk)} disabled={!canManage || busy === lk} data-testid={`plan-subscribe-${plan.id}`}
                      className="w-full gap-2 bg-blue-600 text-white hover:bg-blue-500">
                      {busy === lk ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Subscribe
                    </Button>
                    {plan.trial && (
                      <Button onClick={() => subscribe(lk, true)} disabled={!canManage || busy === lk} variant="outline" data-testid={`plan-trial-${plan.id}`}
                        className="w-full border-emerald-500/40 bg-transparent text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200">
                        Start 2-month free trial
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8" data-testid="billing-history">
        <h2 className="font-display text-sm font-bold uppercase tracking-wider text-slate-200">Billing history</h2>
        <div className="mt-3 overflow-hidden rounded-xl border border-slate-800 bg-[#111827]">
          {invoices.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No invoices yet. Your receipts and invoices appear here after your first payment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="invoices-table">
                <thead>
                  <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-5 py-3 font-semibold">Date</th>
                    <th className="px-5 py-3 font-semibold">Plan</th>
                    <th className="px-5 py-3 font-semibold">Amount</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 text-right font-semibold">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-slate-800/60 last:border-0" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-5 py-3 text-slate-300">{inv.date}</td>
                      <td className="px-5 py-3 text-slate-300">{inv.description}</td>
                      <td className="px-5 py-3 font-mono-data text-slate-100">{inv.amount}</td>
                      <td className="px-5 py-3"><StatusPill value={inv.status} /></td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-3">
                          {inv.hosted_invoice_url && (
                            <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" data-testid={`invoice-view-${inv.id}`}
                              className="inline-flex items-center gap-1 font-semibold text-blue-400 hover:text-blue-300">
                              View <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" data-testid={`invoice-pdf-${inv.id}`}
                              className="inline-flex items-center gap-1 font-semibold text-slate-300 hover:text-white">
                              PDF <Download className="h-3.5 w-3.5" />
                            </a>
                          )}
                          {!inv.hosted_invoice_url && !inv.invoice_pdf && <span className="text-slate-600">—</span>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
